import { getApiBaseUrlWithoutApi } from './apiConfig';
import { toDisplayText } from './displayText';

const API_BASE_URL = getApiBaseUrlWithoutApi();

/**
 * Helper function to construct full avatar URL from relative path
 * @param {string} avatarPath - The avatar path (can be relative or absolute)
 * @param {string} fallbackName - Name for generating fallback avatar
 * @returns {string} Full URL to the avatar image or fallback
 */
export const getAvatarUrl = (avatarPath, fallbackName = null) => {
  if (!avatarPath) {
    // Generate fallback avatar if name is provided
    if (fallbackName) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(fallbackName)}&background=667eea&color=fff&size=200`;
    }
    return null;
  }
  
  // If it's already a full URL, return it
  if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://')) {
    return avatarPath;
  }
  
  // Extract base URL from API_BASE_URL (e.g., http://localhost:5001/api -> http://localhost:5001)
  const baseUrl = API_BASE_URL.replace(/\/api\/?$/, '');
  
  // If it's a relative path starting with /uploads, construct full URL
  if (avatarPath.startsWith('/uploads')) {
    return `${baseUrl}${avatarPath}`;
  }
  
  // If it starts with /, use it as-is
  if (avatarPath.startsWith('/')) {
    return `${baseUrl}${avatarPath}`;
  }
  
  // Otherwise, assume it's relative to uploads directory
  return `${baseUrl}/uploads/${avatarPath}`;
};

/**
 * Get avatar URL from user object with fallback
 * @param {object} user - User object
 * @returns {string} Avatar URL or fallback
 */
export const getUserAvatarUrl = (user) => {
  if (!user) {
    return `https://ui-avatars.com/api/?name=User&background=667eea&color=fff&size=200`;
  }
  
  // Check for avatar path in order of preference - use profile_picture_url first, then avatar_url, then avatar
  const avatarPath = user.profile_picture_url || user.avatar_url || user.avatar;
  // Use consistent name construction; coerce name in case API returns { en, ar }
  const nameStr = toDisplayText(user.name, 'en');
  const userName = nameStr || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'User';
  
  if (avatarPath) {
    const fullUrl = getAvatarUrl(avatarPath);
    if (fullUrl) {
      return fullUrl;
    }
  }
  
  // Fallback to generated avatar - use consistent size and name
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=667eea&color=fff&size=200`;
};

